import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { track, identify } from '../lib/analytics'
import { Eye, EyeOff, AlertCircle, CheckCircle2, Sparkles, ArrowRight, Loader } from 'lucide-react'
import Logo from '../components/ui/Logo'

const INDUSTRIES = [
  'Alimentos y bebidas', 'Agroindustria', 'Manufactura', 'Retail / Comercio',
  'Servicios financieros', 'Salud y farmacéutica', 'Tecnología', 'Construcción e inmobiliaria',
  'Logística y transporte', 'Energía', 'Educación', 'Turismo y hospitalidad',
  'Medios y entretenimiento', 'Consultoría y servicios profesionales', 'Otro',
]

const SIZES = [
  { value: 'pequeña',  label: 'Pequeña (10–50 empleados)' },
  { value: 'mediana',  label: 'Mediana (51–250 empleados)' },
  { value: 'grande',   label: 'Grande (250+ empleados)' },
]

const COUNTRIES = [
  'Colombia', 'México', 'Perú', 'Chile', 'Ecuador', 'Venezuela', 'Argentina',
  'Bolivia', 'Paraguay', 'Uruguay', 'Costa Rica', 'Panamá', 'Guatemala', 'Otro',
]

const STEPS = [
  { id: 1, label: 'Tu empresa' },
  { id: 2, label: 'Tu cuenta' },
  { id: 3, label: '¡Listo!' },
]

export default function RegisterPage() {
  const navigate   = useNavigate()
  const [step, setStep]       = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [showPw, setShowPw]   = useState(false)

  const [form, setForm] = useState({
    // Step 1 – empresa
    company:   '',
    industry:  '',
    size:      '',
    country:   'Colombia',
    // Step 2 – cuenta
    full_name: '',
    email:     '',
    password:  '',
    confirm:   '',
  })

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  function nextStep(e) {
    e.preventDefault()
    setError('')
    if (step === 1) {
      if (!form.company.trim() || !form.industry || !form.size)
        return setError('Completa todos los campos antes de continuar.')
    }
    if (step === 2) {
      if (!form.full_name.trim() || !form.email.trim() || !form.password)
        return setError('Completa todos los campos antes de continuar.')
      if (form.password.length < 8)
        return setError('La contraseña debe tener al menos 8 caracteres.')
      if (form.password !== form.confirm)
        return setError('Las contraseñas no coinciden.')
      return handleRegister()
    }
    setStep(s => s + 1)
  }

  async function handleRegister() {
    setLoading(true)
    setError('')
    try {
      // 1. Create auth user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email:    form.email.trim().toLowerCase(),
        password: form.password,
        options:  { data: { full_name: form.full_name.trim() } },
      })
      if (signUpError) throw signUpError
      const userId = authData.user?.id
      if (!userId) throw new Error('No se pudo crear el usuario.')

      // 2. Insert profile
      const { error: profError } = await supabase.from('profiles').insert({
        id:        userId,
        full_name: form.full_name.trim(),
        email:     form.email.trim().toLowerCase(),
        role:      'caja_user',
      })
      if (profError && !profError.message.includes('duplicate')) throw profError

      // 3. Create consultancy
      const { data: cons, error: consError } = await supabase
        .from('consultancies')
        .insert({
          name:           `Plan Estratégico — ${form.company.trim()}`,
          client_company: form.company.trim(),
          industry:       form.industry,
          status:         'active',
          is_caja:        true,
        })
        .select()
        .single()
      if (consError) throw consError

      // 4. Link user to consultancy
      await supabase.from('consultancy_users').insert({
        consultancy_id: cons.id,
        user_id:        userId,
      }).select()

      // 5. Create full NVC project structure via RPC
      const { error: rpcError } = await supabase.rpc('create_project_stages', {
        p_consultancy_id: cons.id,
      })
      if (rpcError) throw rpcError

      // 6. Save extra metadata in project_ficha
      await supabase.from('project_ficha').upsert({
        consultancy_id:     cons.id,
        tamano_empresa:     form.size,
        descripcion_negocio: `Empresa: ${form.company.trim()}\nIndustria: ${form.industry}\nPaís: ${form.country}`,
      }, { onConflict: 'consultancy_id' })

      // 7. Send welcome email — non-blocking. We deliberately don't await this:
      // a Resend outage or missing API key must never break the signup flow.
      supabase.functions.invoke('send-welcome-email', {
        body: {
          to:        form.email.trim().toLowerCase(),
          full_name: form.full_name.trim(),
          company:   form.company.trim(),
          projectId: cons.id,
        },
      }).catch(err => console.warn('welcome email failed (non-blocking):', err))

      // 8. Analytics: identify + signup_completed (no-op if VITE_POSTHOG_KEY unset)
      identify(userId, {
        email:    form.email.trim().toLowerCase(),
        name:     form.full_name.trim(),
        company:  form.company.trim(),
        industry: form.industry,
        country:  form.country,
        size:     form.size,
        plan:     'trial',
      })
      track('caja_signup_completed', {
        industry: form.industry,
        size:     form.size,
        country:  form.country,
      })

      // Done — show success then redirect
      setStep(3)
      setTimeout(() => navigate('/caja'), 2000)
    } catch (err) {
      console.error(err)
      setError(err.message ?? 'Ocurrió un error inesperado. Por favor intenta de nuevo.')
      setLoading(false)
    }
  }

  // ── Step 3 — success screen ──────────────────────────────────
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-700 to-brand-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 size={32} className="text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Cuenta creada!</h2>
          <p className="text-gray-500 text-sm mb-1">Tu proyecto estratégico está listo.</p>
          <p className="text-gray-400 text-xs">Redirigiendo en un momento…</p>
          <div className="mt-6 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-600 border-t-transparent" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-800 via-brand-700 to-brand-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Brand header con logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo variant="mark" className="text-cream-100 mb-3" size={56} />
          <h1 className="font-display text-2xl text-cream-100 tracking-wider leading-none mb-1">NEXT VALLEY</h1>
          <p className="text-accent-400 text-[10px] font-semibold uppercase tracking-[0.3em] mb-2">Strategic Partners</p>
          <span className="text-xs font-medium text-accent-400 bg-accent-500/15 border border-accent-500/30 px-3 py-1 rounded-full uppercase tracking-wider">
            Estrategia en una Caja
          </span>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEPS.filter(s => s.id < 3).map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full transition-all ${
                step === s.id
                  ? 'bg-white text-brand-700'
                  : step > s.id
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/10 text-white/50'
              }`}>
                {step > s.id ? <CheckCircle2 size={12} /> : null}
                {s.label}
              </div>
              {i < STEPS.length - 2 && <ArrowRight size={12} className="text-white/30" />}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">

          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-5 text-sm">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={nextStep} className="space-y-4">

            {/* ── STEP 1: Empresa ── */}
            {step === 1 && (
              <>
                <div className="mb-2">
                  <h2 className="text-xl font-bold text-gray-900">Cuéntanos sobre tu empresa</h2>
                  <p className="text-gray-400 text-sm mt-1">Esta información guiará el análisis estratégico.</p>
                </div>
                <div>
                  <label className="label">Nombre de la empresa *</label>
                  <input className="input" required autoFocus
                    value={form.company}
                    onChange={e => set('company', e.target.value)}
                    placeholder="Ej: Grupo Industrial Andino S.A.S." />
                </div>
                <div>
                  <label className="label">Industria *</label>
                  <select className="input" value={form.industry} onChange={e => set('industry', e.target.value)} required>
                    <option value="">Selecciona una industria…</option>
                    {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Tamaño de la empresa *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {SIZES.map(s => (
                      <button key={s.value} type="button"
                        onClick={() => set('size', s.value)}
                        className={`text-xs py-2.5 px-2 rounded-xl border-2 font-medium transition-all text-center leading-tight ${
                          form.size === s.value
                            ? 'border-brand-600 bg-brand-50 text-brand-700'
                            : 'border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label">País</label>
                  <select className="input" value={form.country} onChange={e => set('country', e.target.value)}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </>
            )}

            {/* ── STEP 2: Cuenta ── */}
            {step === 2 && (
              <>
                <div className="mb-2">
                  <h2 className="text-xl font-bold text-gray-900">Crea tu cuenta</h2>
                  <p className="text-gray-400 text-sm mt-1">Con estas credenciales podrás acceder a tu proyecto.</p>
                </div>
                <div>
                  <label className="label">Tu nombre completo *</label>
                  <input className="input" required autoFocus
                    value={form.full_name}
                    onChange={e => set('full_name', e.target.value)}
                    placeholder="Ej: María Fernanda Ríos" />
                </div>
                <div>
                  <label className="label">Correo electrónico *</label>
                  <input type="email" className="input" required
                    value={form.email}
                    onChange={e => set('email', e.target.value)}
                    placeholder="tu@empresa.com" />
                </div>
                <div>
                  <label className="label">Contraseña *</label>
                  <div className="relative">
                    <input type={showPw ? 'text' : 'password'} className="input pr-10" required
                      value={form.password}
                      onChange={e => set('password', e.target.value)}
                      placeholder="Mínimo 8 caracteres" />
                    <button type="button" onClick={() => setShowPw(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label">Confirmar contraseña *</label>
                  <input type="password" className="input" required
                    value={form.confirm}
                    onChange={e => set('confirm', e.target.value)}
                    placeholder="Repite tu contraseña" />
                </div>
              </>
            )}

            <button type="submit" disabled={loading}
              className="btn-primary w-full py-3 text-base mt-2 flex items-center justify-center gap-2">
              {loading
                ? <><Loader size={16} className="animate-spin" /> Creando tu proyecto…</>
                : step === 1
                ? <>Continuar <ArrowRight size={16} /></>
                : <>Crear mi proyecto <Sparkles size={16} /></>
              }
            </button>

            {step === 2 && (
              <button type="button" onClick={() => setStep(1)}
                className="w-full text-sm text-gray-400 hover:text-gray-600 py-1 transition-colors">
                ← Volver
              </button>
            )}
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-brand-600 hover:underline font-medium">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
